import _ from 'lodash'
import { createSelector } from 'reselect'
import { Dictionary, StepSchema, UserProfile } from '@feedyou/schema'
import { isNotEmptyElement } from '@feedyou/utils'

import {
  AuthorId,
  Comment,
  CommentId,
  CommentsById,
  isCommentResolved,
  isCommentValidForSubmitOnServer,
  isDirectBotComment,
  isDirectIntentComment,
  isDirectStepComment,
  isTopLevelComment,
  serverAuthorsByIdSelector,
  serverCommentsByIdSelector
} from '../../../features/serverComments'
import {
  draftCommentsSelector,
  pendingEditsTextForCommentIdSelector
} from '../../../store/comment/selectors'
import { allStepsSelector } from '../../../store/flow/selectors/selectors'
import { isCommentRelatingToNonExistentStep } from './utils'
import { RootState } from '../../../reducers'
import { authorForCurrentUserSelector } from '../../../store/user'

export const nonObsoleteCommentsByIdSelector = createSelector(
  serverCommentsByIdSelector,
  draftCommentsSelector,
  allStepsSelector,
  (allCommentsFromServer, draftComments, existingSteps): CommentsById => {
    if (!allCommentsFromServer) {
      return {}
    }

    const allComments = { ...allCommentsFromServer, ...draftComments }

    const existingStepIds = existingSteps.map((step: StepSchema) => step.id)

    const isCommentObsolete = isCommentRelatingToNonExistentStep.bind(
      null,
      allComments,
      existingStepIds
    )

    return _.omitBy(allComments, comment => isCommentObsolete(comment!.id))
  }
)

export const localAuthorsSelectors = createSelector(
  authorForCurrentUserSelector, // I <3 PRETTIER
  (currentUserAuthor): Dictionary<AuthorId, UserProfile> => ({
    [currentUserAuthor?.id!]: currentUserAuthor
  })
)

export const commentAuthorsSelector = createSelector(
  serverAuthorsByIdSelector,
  localAuthorsSelectors,
  (serverAuthors, localAuthors): Dictionary<AuthorId, UserProfile> => {
    return _.merge({}, serverAuthors, localAuthors)
  }
)

export const commentByIdSelector = (state: RootState, commentId?: CommentId) => {
  return nonObsoleteCommentsByIdSelector(state)[commentId!]
}

export const replyCommentsByInReplyToSelector = (state: RootState, parentCommentId: CommentId) => {
  return Object.values(nonObsoleteCommentsByIdSelector(state))
    ?.filter(comment => comment?.inReplyTo === parentCommentId)
    .filter(isNotEmptyElement)
}

export const directBotCommentsSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  allComments => {
    return Object.values(allComments).filter(isDirectBotComment)
  }
)

export const directStepCommentsByStepIdSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  (allComments): Dictionary<string, Comment[]> => {
    const directStepComments = Object.values(allComments).filter(isDirectStepComment)

    return _.groupBy(directStepComments, 'stepId')
  }
)

export const directIntentCommentsByIntentIdSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  (allComments): Dictionary<string, Comment[]> => {
    const directIntentComments = Object.values(allComments).filter(isDirectIntentComment)

    return _.groupBy(directIntentComments, 'intentId')
  }
)

export const repliesCommentsByTargetSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  (allComments): Dictionary<CommentId, Comment[]> => {
    return _.groupBy(allComments, 'inReplyTo') as Dictionary<CommentId, Comment[]>
  }
)

export const resolvedTopLevelCommentsSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  allComments => {
    return Object.values(allComments) // I <3 PRETTIER
      .filter(isTopLevelComment)
      .filter(isCommentResolved)
  }
)

export const unresolvedTopLevelCommentsSelector = createSelector(
  nonObsoleteCommentsByIdSelector,
  allComments => {
    return Object.values(allComments)
      .filter(isTopLevelComment)
      .filter(comment => !isCommentResolved(comment))
  }
)

export const isCommentValidForSubmitToServerSelector = createSelector(
  commentByIdSelector,
  pendingEditsTextForCommentIdSelector,
  (commentWithoutEdits, pendingText) => {
    if (!pendingText || !commentWithoutEdits) return false
    return isCommentValidForSubmitOnServer({ ...commentWithoutEdits, text: pendingText })
  }
)
